import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  ArrowRight,
  Phone,
  Mail,
  MessageSquare,
  Download,
  Users,
  Award
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CTASection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    interest: 'quote'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API}/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          phone: formData.phone,
          inquiry_type: formData.interest,
          message: formData.message,
          urgency: 'normal',
          source: 'website'
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        alert(result.message || 'Thank you for your inquiry! We will contact you within 24 hours.');
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: '',
          interest: 'quote'
        });
      } else {
        throw new Error(result.message || 'Failed to submit form');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your form. Please try again or call us at (706) 278-3383.');
    }
  };

  const ctaOptions = [
    {
      icon: MessageSquare,
      title: "Request Quote",
      description: "Get custom pricing for your specific needs",
      action: "Get Quote",
      color: "automats-red",
      bgColor: "bg-red-50",
      href: "mailto:sales@automats.us?subject=Quote%20Request&body=Hello%2C%20I%20would%20like%20to%20request%20a%20quote%20for%20AutoMats%20products."
    },
    {
      icon: Users,
      title: "Become a Dealer",
      description: "Join our network of successful partners",
      action: "Apply Now",
      color: "automats-eco-green",
      bgColor: "bg-green-50",
      href: "mailto:sales@automats.us?subject=Dealer%20Program%20Inquiry&body=Hello%2C%20I%20am%20interested%20in%20becoming%20an%20AutoMats%20dealer.%20Please%20send%20me%20more%20information."
    },
    {
      icon: Download,
      title: "Request Samples",
      description: "See and feel the quality for yourself",
      action: "Order Samples",
      color: "blue-500",
      bgColor: "bg-blue-50",
      href: "mailto:sales@automats.us?subject=Sample%20Request&body=Hello%2C%20I%20would%20like%20to%20request%20product%20samples%20to%20evaluate."
    }
  ];

  return (
    <section className="py-20 lg:py-24 bg-transparent">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="bg-automats-red/10 text-automats-red border-automats-red/20 mb-4">
            Take Action Today
          </Badge>
          <h2 className="text-4xl lg:text-5xl font-semibold text-automats-black mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-automats-black max-w-3xl mx-auto leading-relaxed">
            Join hundreds of dealers who've discovered the AutoMats advantage. 
            Better products, better margins, better partnerships.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left Side - CTA Options */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-heading font-bold text-automats-black mb-6 text-center lg:text-left">
                Choose Your Next Step
              </h3>
              
              <div className="space-y-6">
                {ctaOptions.map((option, index) => {
                  const IconComponent = option.icon;
                  return (
                    <Card key={index} className="bg-white border-gray-200 hover:border-automats-red/30 hover:shadow-lg transition-all duration-300 cursor-pointer group">
                      <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                          <div className="bg-automats-off-white p-3 rounded-full group-hover:bg-automats-red/10 transition-colors">
                            <IconComponent className={`h-6 w-6 text-${option.color} group-hover:text-automats-red transition-colors`} />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-lg font-heading font-semibold text-automats-black mb-1">
                              {option.title}
                            </h4>
                            <p className="text-automats-steel text-sm">
                              {option.description}
                            </p>
                          </div>
                          <a 
                            href={option.href}
                            className="bg-automats-red hover:bg-red-700 text-white group-hover:scale-105 transition-all px-4 py-2 rounded-md text-sm font-medium inline-flex items-center"
                          >
                            {option.action}
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </a>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Enhanced Contact Info */}
            <Card className="bg-automats-off-white border-automats-red/20">
              <CardContent className="p-6">
                <h4 className="text-xl font-heading font-bold text-automats-black mb-4">
                  Need to Talk Right Now?
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 text-automats-steel">
                    <Phone className="h-5 w-5 text-automats-red" />
                    <span className="font-body">800-570-0704</span>
                  </div>
                  <div className="flex items-center space-x-3 text-automats-steel">
                    <Mail className="h-5 w-5 text-automats-red" />
                    <span className="font-body">sales@automats.us</span>
                  </div>
                  <div className="flex items-center space-x-3 text-automats-steel">
                    <Award className="h-5 w-5 text-automats-red" />
                    <span className="font-body">Mon-Fri: 8:00 AM - 5:00 PM EST</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Side - Clean Contact Form */}
          <Card className="bg-white border-gray-200 shadow-xl">
            <CardHeader className="bg-automats-off-white border-b border-gray-100">
              <CardTitle className="text-2xl font-heading text-automats-black">
                Get Started Today
              </CardTitle>
              <p className="text-automats-steel font-body">
                Fill out the form below and we'll get back to you within 24 hours.
              </p>
            </CardHeader>
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-body font-medium text-automats-black mb-2">
                      Full Name *
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="border-gray-300 focus:border-automats-red focus:ring-automats-red/20"
                      placeholder="Your name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-body font-medium text-automats-black mb-2">
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="border-gray-300 focus:border-automats-red focus:ring-automats-red/20"
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-body font-medium text-automats-black mb-2">
                      Company Name
                    </label>
                    <Input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="border-gray-300 focus:border-automats-red focus:ring-automats-red/20"
                      placeholder="Your company"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-body font-medium text-automats-black mb-2">
                      Phone Number
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="border-gray-300 focus:border-automats-red focus:ring-automats-red/20"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-automats-black mb-2">
                    I'm interested in:
                  </label>
                  <select
                    name="interest"
                    value={formData.interest}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 focus:border-automats-red focus:ring-automats-red/20 rounded-md px-3 py-2 font-body"
                  >
                    <option value="quote">Request Quote</option>
                    <option value="dealer">Becoming a Dealer</option>
                    <option value="sample">Request Samples</option>
                    <option value="general">General Information</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-automats-black mb-2">
                    Message
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="border-gray-300 focus:border-automats-red focus:ring-automats-red/20 min-h-[100px]"
                    placeholder="Tell us about your needs..."
                  />
                </div>

                <Button 
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-automats-red hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-lg py-6 font-body font-semibold"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  {!isSubmitting && <ArrowRight className="ml-2 h-5 w-5" />}
                </Button>

                <p className="text-sm text-automats-steel text-center font-body">
                  By submitting this form, you agree to be contacted by AutoMats. 
                  We respect your privacy and never share your information.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default CTASection;